/**
 * A circle or square to place; `r` is its radius (half-size) in pixels.
 * `space` (optional) is how close other nodes may come – larger than `r` when a label sits under it.
 */
export type LayoutNode = { id: string; r: number; space?: number }
export type LayoutLink = { source: string; target: string }
export type Placed = { x: number; y: number }

const ITERATIONS = 500
const LINK_LENGTH = 80
const REPULSION = 9000
const SPRING = 0.05
const GRAVITY = 0.01
const PADDING = 8

/**
 * A small force-directed layout, run to completion before drawing:
 *  - every pair of nodes pushes apart (so they don't pile up),
 *  - every link pulls its two ends together like a spring,
 *  - a gentle pull keeps everything near the middle, and nodes never overlap or leave the box.
 * Starting positions come from a fixed seed, so the same data always gives the same picture.
 */
export function forceLayout(
  nodes: LayoutNode[],
  links: LayoutLink[],
  width: number,
  height: number,
): Map<string, Placed> {
  const random = seededRandom(42)
  const pos = nodes.map((n) => ({
    ...n,
    x: width / 2 + (random() - 0.5) * width * 0.8,
    y: height / 2 + (random() - 0.5) * height * 0.8,
  }))
  const index = new Map(pos.map((p, i) => [p.id, i]))
  const edges = links
    .map((l) => [index.get(l.source), index.get(l.target)] as const)
    .filter((e): e is readonly [number, number] => e[0] !== undefined && e[1] !== undefined)

  for (let step = 0; step < ITERATIONS; step++) {
    const heat = 1 - step / ITERATIONS // moves get smaller as the layout settles

    // Push every pair apart.
    for (let i = 0; i < pos.length; i++) {
      for (let j = i + 1; j < pos.length; j++) {
        const a = pos[i]
        const b = pos[j]
        let dx = b.x - a.x
        let dy = b.y - a.y
        const d2 = Math.max(dx * dx + dy * dy, 1)
        const d = Math.sqrt(d2)
        const force = (REPULSION / d2) * heat
        dx /= d
        dy /= d
        a.x -= dx * force
        a.y -= dy * force
        b.x += dx * force
        b.y += dy * force

        // Never overlap (including the room kept for labels).
        const minGap = (a.space ?? a.r) + (b.space ?? b.r) + PADDING
        if (d < minGap) {
          const shift = (minGap - d) / 2
          a.x -= dx * shift
          a.y -= dy * shift
          b.x += dx * shift
          b.y += dy * shift
        }
      }
    }

    // Pull linked nodes together.
    for (const [i, j] of edges) {
      const a = pos[i]
      const b = pos[j]
      const dx = b.x - a.x
      const dy = b.y - a.y
      const d = Math.max(Math.sqrt(dx * dx + dy * dy), 1)
      const pull = ((d - LINK_LENGTH) / d) * SPRING * heat
      a.x += dx * pull
      a.y += dy * pull
      b.x -= dx * pull
      b.y -= dy * pull
    }

    // Drift towards the middle (much weaker sideways, so a wide canvas gets used), and stay inside.
    for (const p of pos) {
      p.x += (width / 2 - p.x) * GRAVITY * heat * 0.2
      p.y += (height / 2 - p.y) * GRAVITY * heat
      const halfWidth = Math.max(p.r, (p.space ?? p.r) * 0.9) // labels are wider than their circle
      p.x = Math.min(Math.max(p.x, halfWidth + 4), width - halfWidth - 4)
      p.y = Math.min(Math.max(p.y, p.r + 4), height - p.r - 18) // room for a label underneath
    }
  }

  return new Map(pos.map((p) => [p.id, { x: p.x, y: p.y }]))
}

/** Same seed → same sequence of "random" numbers → same layout every time. */
function seededRandom(seed: number): () => number {
  let s = seed >>> 0
  return () => {
    s = (s + 0x6d2b79f5) >>> 0
    let t = s
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}
